import React, { useState, useEffect } from "react";
import {
  Create,
  TextInput,
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  useDataProvider,
  useRedirect,
  useRefresh,
  SelectInput,
  CreateProps,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, Typography } from "@material-ui/core";
import TitleBreadcrumb from "components/TitleBreadcrumb";
import EditableDataGrid from "./components/EditableAreaDatagrid";
import { AcademicYearResources, GetParam } from "utils/tools";
import CustomCreateToolbar from "components/CustomCreateToolbar";
import { labelDataGridStyles } from "utils/style";
import CustomEndDateInput from "components/CustomEndDateInput";
import moment from "moment";
import { useField } from "react-final-form";
import CustomDatePickerInput from "components/CustomDatePickerInput";
import TbfCodeInput from "./components/TbfCodeInput";

interface ICustomReferenceInput {
  startMinDate: string;
  schoolId: string;
  callback: (x: string) => void;
}

const CustomReferenceInput = (props: ICustomReferenceInput) => {
  const startedAt = useField("startedAt");
  const endedAt = useField("endedAt");
  const invoiceIssuedAt = useField("invoiceIssuedAt");

  // startedAt.input.onChange(props.startMinDate);

  return (
    <ReferenceInput
      label="Select Academic Year"
      source="academicYearId"
      reference="academicYears"
      validate={[required()]}
      filter={{ SchoolId: Number(props.schoolId) }}
      onChange={({ target }) => {
        props.callback(target.value);

        startedAt.input.onChange("");
        endedAt.input.onChange("");
        invoiceIssuedAt.input.onChange("");
      }}
    >
      <SelectInput optionText="name" optionId="id" />
    </ReferenceInput>
  );
};

const RateChartCreate = (props: CreateProps) => {
  const [areas, setAreas] = useState<IArea[]>([]);
  const [minDate, setMinDate] = useState<string>();
  // const [maxDate, setMaxDate] = useState<string>();
  const [idAcademicYear, setIdAcademicYear] = useState<number>();

  const provider = useDataProvider();
  const param = GetParam();
  const redirect = useRedirect();
  const refresh = useRefresh();

  const breadcrumb = [
    { title: "Schools", link: `/schools` },
    { title: "Pricing", link: `/schools/${param.get("schoolId")}/show/5` },
    { title: "Create", link: "" },
  ];

  //fetch area data for current school on first load
  useEffect(() => {
    provider
      .getList("areas", {
        filter: { SchoolId: Number(param.get("schoolId")) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "MaxDistanceInMeters",
          order: "ASC",
        },
      })
      .then((result) => {
        setAreas(result.data as IArea[]);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const transform = (data: any) => {
    data.rateChartItems = [
      ...data.oneTimeAWeekPricing,
      ...data.twoTimeAWeekPricing,
      ...data.threeTimeAWeekPricing,
      ...data.fourTimeAWeekPricing,
      ...data.fiveTimeAWeekPricing,
    ];

    data.status = "active";

    delete data.oneTimeAWeekPricing;
    delete data.twoTimeAWeekPricing;
    delete data.threeTimeAWeekPricing;
    delete data.fourTimeAWeekPricing;
    delete data.fiveTimeAWeekPricing;

    return data;
  };

  const CustomRedirect = (props: { data: IRateChart }) => {
    redirect(`/schools/${props.data.schoolId}/show/5`);
    refresh();
  };

  const academicYearResources = AcademicYearResources(idAcademicYear);

  useEffect(() => {
    if (idAcademicYear !== undefined) {
      provider
        .getList("rateCharts", {
          filter: { AcademicYearId: Number(idAcademicYear) },
          pagination: {
            page: 1,
            perPage: 1,
          },
          sort: {
            field: "EndedAt",
            order: "Desc",
          },
        })
        .then((result) => {
          if (result.total > 0) {
            setMinDate(
              moment
                .utc(result.data[0].endedAt)
                .add(1, "d")
                .format("yyyy-MM-DD")
            );

            // setMaxDate(
            //   moment
            //     .utc(result.data[0].endedAt)
            //     .add(1, "d")
            //     .format("yyyy-MM-DD")
            // );
          } else {
            setMinDate(
              moment.utc(academicYearResources?.startDate).format("yyyy-MM-DD")
            );
            // setMaxDate(
            //   moment.utc(academicYearResources?.endDate).format("yyyy-MM-DD")
            // );
          }
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idAcademicYear]);

  const academicYearInputCallback = (id: any) => {
    setIdAcademicYear(id);
  };

  return (
    <>
      <TitleBreadcrumb paths={breadcrumb} />
      <Typography variant="h4">New Rate Chart</Typography>
      <Create transform={transform} onSuccess={CustomRedirect} {...props}>
        <SimpleForm toolbar={<CustomCreateToolbar />}>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School & Academic Year</Typography>
            </Grid>

            <Grid item xs={3}>
              <ReferenceInput
                label="Select School"
                source="schoolId"
                reference="schools"
                validate={[required()]}
                disabled
              >
                <AutocompleteInput optionText="name" optionId="id" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <CustomReferenceInput
                callback={academicYearInputCallback}
                startMinDate={minDate}
                schoolId={param.get("schoolId")}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <TbfCodeInput />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Pricing Details</Typography>
            </Grid>

            <Grid item xs={3}>
              <TextInput
                source="name"
                label="Pricing Name"
                validate={required()}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <CustomEndDateInput
                source="startedAt"
                label="Pricing Start Date"
                parentStartDate={minDate}
                parentEndDate={moment
                  .utc(academicYearResources?.endDate)
                  .format("yyyy-MM-DD")}
              />
            </Grid>

            <Grid item xs={3}>
              <CustomEndDateInput
                source="endedAt"
                label="Pricing End Date"
                parentEndDate={moment
                  .utc(academicYearResources?.endDate)
                  .format("yyyy-MM-DD")}
                comparison="startedAt"
              />
            </Grid>
            <Grid item xs={6}></Grid>

            <Grid item xs={3}>
              <CustomDatePickerInput
                name="invoiceIssuedAt"
                label="Invoice Issue Date"
                min={moment().format("Y-MM-DD")}
              />
            </Grid>

            {/* <Grid item xs={4}>
              <SelectInput
                source="isIncludeTax"
                label="Tax Included?"
                choices={[
                  { id: true, name: "Yes" },
                  { id: false, name: "No" },
                ]}
              />
            </Grid> */}

            <Grid item xs={12}>
              <Typography variant="h6">
                Area Pricing (Inclusive of GST)
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  1 Time a Week
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="oneTimeAWeekPricing"
                  frequency={1}
                />
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  2 Time a Week
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="twoTimeAWeekPricing"
                  frequency={2}
                />
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  3 Time a Week
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="threeTimeAWeekPricing"
                  frequency={3}
                />
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  4 Time a Week
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="fourTimeAWeekPricing"
                  frequency={4}
                />
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={labelDataGridStyles}>
                  5 Time a Week
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <EditableDataGrid
                  areas={areas}
                  name="fiveTimeAWeekPricing"
                  frequency={5}
                />
              </Grid>
            </Grid>
          </SanitizedGrid>
        </SimpleForm>
      </Create>
    </>
  );
};

export default RateChartCreate;
